import { useMemo, useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@repo/ui/src/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import { cn } from '@repo/ui/src/lib/utils';
import type { Client } from '@repo/shared-types';
import { givenNameOf } from '../lib/studentName';
import { normalizeForSearch } from '../lib/textSearch';

type ClientOption = Pick<Client, 'id' | 'name' | 'zaloPhone'>;

/**
 * Searchable payer/client picker — the shared combobox behind every place the
 * teacher picks a payer: create/edit student, and create/edit manual invoice.
 * Modeled directly on `AddStudentCombobox`: shadcn Command-in-Popover,
 * `shouldFilter={false}` with `normalizeForSearch` applied to both the query
 * and the candidate text (see that component's doc comment for why cmdk's own
 * fuzzy filter can't be used here), sorted by given name with `'vi'`
 * collation.
 *
 * Two things this adds on top of that pattern, both because two mothers named
 * Trần Thị Huyền are a real occurrence at 80 students and the teacher tells
 * them apart by Zalo number, not by name:
 *  - the option label appends the phone in brackets when the client has one;
 *  - the filter also matches on phone digits, so typing a number finds the
 *    right person even when the name alone wouldn't disambiguate.
 *
 * Unlike `AddStudentCombobox` (fixed copy from the `scheduling` namespace,
 * one call site) this is used from `students`, `invoices` and — via its call
 * sites — potentially any namespace, so all copy is passed in by the caller
 * rather than looked up here.
 */
export function ClientCombobox({
  options, value, onSelect, disabled, placeholder, searchPlaceholder, emptyText,
}: {
  options: ClientOption[];
  value: string;
  onSelect: (clientId: string) => void;
  disabled?: boolean;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const sorted = useMemo(
    () => [...options].sort((a, b) => givenNameOf(a.name).localeCompare(givenNameOf(b.name), 'vi')),
    [options],
  );
  const filtered = useMemo(() => {
    const needle = normalizeForSearch(search);
    if (!needle) return sorted;
    // Digits from the query only match digits in the phone — a name that
    // happens to contain "3" shouldn't start matching phone numbers, so this
    // arm is skipped entirely when the query has no digits at all.
    const needleDigits = search.replace(/\D/g, '');
    return sorted.filter((c) => {
      if (normalizeForSearch(c.name).includes(needle)) return true;
      if (!needleDigits) return false;
      const phoneDigits = (c.zaloPhone ?? '').replace(/\D/g, '');
      return phoneDigits.length > 0 && phoneDigits.includes(needleDigits);
    });
  }, [sorted, search]);

  const selected = options.find((c) => c.id === value);
  const labelOf = (c: ClientOption) => (c.zaloPhone ? `${c.name} (${c.zaloPhone})` : c.name);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? labelOf(selected) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  onSelect={() => {
                    onSelect(c.id);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  {labelOf(c)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
